import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { EmberField } from '@/components/shared/EmberField'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'
import { generateSanctumDNASummary } from '@/services/ai'
import { getDestinationImages, type Img } from '@/services/images'
import { Attribution } from '@/components/shared/Attribution'
import { openDeal, NETWORK_LABELS } from '@/services/affiliates'
import type { Destination } from '@/types'

// Photo hero with graceful gradient+emoji fallback — never a broken <img>,
// never a layout jump while images resolve.
function PhotoHero({ dest, height, ratio, children }: { dest: Destination; height?: number; ratio?: string; children?: React.ReactNode }) {
  const [img, setImg] = useState<Img | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setImg(null); setFailed(false)
    let cancelled = false
    getDestinationImages(dest, 1).then((imgs) => {
      if (!cancelled && imgs[0]) setImg(imgs[0])
    })
    return () => { cancelled = true }
  }, [dest.id])

  const showPhoto = img && !failed

  const boxStyle: React.CSSProperties = ratio
    ? { aspectRatio: ratio, position: 'relative', overflow: 'hidden', background: dest.gradient }
    : { height, position: 'relative', overflow: 'hidden', background: dest.gradient }
  return (
    <div style={boxStyle}>
      {showPhoto && (
        <img
          src={img!.url}
          alt={img!.alt || dest.name}
          onError={() => setFailed(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.78) brightness(0.7)' }}
        />
      )}
      {showPhoto && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,14,8,0.25), rgba(5,14,8,0.75))' }} />}
      {!showPhoto && (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, var(--forest2), var(--void))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: (height ?? 120) * 0.32 }}>
          {dest.emoji}
        </div>
      )}
      {children}
      {showPhoto && <Attribution image={img ?? undefined} style={{ position: 'absolute', bottom: 4, right: 8 }} />}
    </div>
  )
}

// 2×2 thumbnail gallery — silently renders nothing (not even a placeholder
// box) if no images resolve, per spec's graceful-degradation requirement.
function DestinationGallery({ dest }: { dest: Destination }) {
  const [imgs, setImgs] = useState<Img[]>([])

  useEffect(() => {
    let cancelled = false
    getDestinationImages(dest, 4).then((res) => { if (!cancelled) setImgs(res) })
    return () => { cancelled = true }
  }, [dest.id])

  if (imgs.length === 0) return null

  return (
    <div>
      <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 8 }}>
        ◈ Destination Imagery
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {imgs.slice(0, 4).map((img, i) => (
          <div key={i} style={{ position: 'relative', aspectRatio: '4 / 3', overflow: 'hidden', border: '1px solid var(--hairline)' }}>
            <img
              src={img.thumb || img.url}
              alt={img.alt || dest.name}
              loading="lazy"
              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none' }}
              style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.8) brightness(0.78)' }}
            />
          </div>
        ))}
      </div>
      <Attribution image={imgs[0]} style={{ display: 'block', marginTop: 6 }} />
    </div>
  )
}

export function DnaScreen() {
  const { dnaKey, answers, setSelectedDestination, setScreen, aiSummary, setAiSummary } = useAppStore()
  const [selected, setSelected] = useState<Destination | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const expandedRef = useRef<HTMLDivElement>(null)

  const profile = DNA_PROFILES[dnaKey]
  const dests = DESTINATIONS[dnaKey] ?? []

  useEffect(() => { if (dests.length > 0) setSelected(dests[0]) }, [dnaKey])

  useEffect(() => {
    if (!profile || answers.length === 0 || aiSummary) return
    setAiLoading(true)
    generateSanctumDNASummary(profile.name, answers.map((a) => a.label), dests.map((d) => d.name))
      .then((s) => { setAiSummary(s); setAiLoading(false) })
      .catch(() => setAiLoading(false))
  }, [dnaKey])

  const pickDest = (d: Destination) => {
    setSelected(d)
    setTimeout(() => expandedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100)
  }

  const goJourney = () => { if (selected) { setSelectedDestination(selected); setScreen('journey') } }

  if (!profile) return null
  const desc = aiSummary || profile.description

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)' }}>
      <Topbar backTo="quiz" />

      <div style={{ width: '100%', maxWidth: 760, margin: '0 auto' }}>
      {/* DNA Hero */}
      <div style={{ padding: '30px 22px 24px', borderBottom: '1px solid var(--hairline)', position: 'relative', overflow: 'hidden', background: 'linear-gradient(165deg, #0E2717 0%, #081C10 55%, #050E08 100%)' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 20% 0%, rgba(201,151,58,0.08) 0%, transparent 55%)', pointerEvents: 'none' }} />
        <EmberField count={12} />

        <div style={{ position: 'relative' }}>
          <div style={{ display: 'inline-block', fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.15em', color: 'var(--candle2)', border: '1px solid var(--hairline2)', padding: '4px 12px', marginBottom: 14, textTransform: 'uppercase' }}>
            ◈ Your Sacred DNA
          </div>

          <motion.h1 initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            style={{ fontFamily: 'var(--serif-caps)', fontSize: 'clamp(24px, 4vw, 34px)', color: 'var(--ivory)', lineHeight: 1.2, marginBottom: 10, letterSpacing: '0.04em' }}
            dangerouslySetInnerHTML={{ __html: profile.title }} />

          {aiLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 48 }}>
              {[0, 1, 2].map((i) => (
                <motion.div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--candle2)' }}
                  animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.28 }} />
              ))}
              <span style={{ fontFamily: 'var(--serif-body)', fontSize: 13, color: 'var(--ivory-faint)', fontStyle: 'italic' }}>
                Sanctum is listening…
              </span>
            </div>
          ) : (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
              style={{ fontFamily: 'var(--serif-body)', fontSize: 15, color: 'var(--ivory-dim)', lineHeight: 1.8, maxWidth: 460, marginBottom: 14, fontStyle: 'italic' }}>
              {desc}
            </motion.p>
          )}

          {aiSummary && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(201,151,58,0.08)', border: '1px solid var(--hairline2)', padding: '3px 10px', marginBottom: 10 }}>
              <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.12em', color: 'var(--candle2)', textTransform: 'uppercase' }}>◈ Personalised by Claude AI</span>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {answers.map((a, i) => (
              <span key={i} style={{ fontFamily: 'var(--serif-body)', fontSize: 12, padding: '4px 10px', border: '1px solid var(--hairline)', color: 'var(--ivory-dim)', background: 'rgba(248,245,239,0.04)', fontStyle: 'italic' }}>
                {a.emoji} {a.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Destinations */}
      <div style={{ padding: '0 18px' }}>
        <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.14em', color: 'var(--ivory-faint)', textTransform: 'uppercase', padding: '18px 0 10px' }}>
          Sacred destinations matched to your calling — select to explore
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
          {dests.map((d) => (
            <motion.button key={d.id} onClick={() => pickDest(d)}
              whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}
              style={{
                border: selected?.id === d.id ? '2px solid var(--candle)' : '1px solid var(--hairline)',
                background: 'var(--forest2)', overflow: 'hidden', cursor: 'pointer', textAlign: 'left',
                boxShadow: selected?.id === d.id ? '0 4px 20px rgba(201,151,58,0.25)' : '0 4px 14px rgba(0,0,0,0.35)',
              }}>
              <PhotoHero dest={d} height={80} />
              <div style={{ padding: '9px 10px', borderTop: '1px solid var(--hairline)' }}>
                {d.country && (
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 2 }}>
                    {d.flag} {d.country}
                  </div>
                )}
                <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 13, color: 'var(--ivory)', letterSpacing: '0.04em' }}>{d.name}</div>
                <div style={{ fontFamily: 'var(--serif-body)', fontSize: 10, color: 'var(--sage)', marginTop: 2, fontStyle: 'italic' }}>{d.faith}</div>
                {d.stops.length > 0 && (
                  <div style={{ fontFamily: 'var(--serif-body)', fontSize: 9, color: 'var(--ivory-faint)', marginTop: 3, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.stops.slice(0, 3).map((s) => s.name.split(' — ')[0].split(',')[0]).join(' · ')}
                  </div>
                )}
                <div style={{ display: 'inline-block', fontFamily: 'var(--serif-caps)', fontSize: 8, padding: '2px 7px', background: 'var(--candle)', color: 'var(--forest)', marginTop: 4, letterSpacing: '0.06em' }}>Save {d.saving}</div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Expanded */}
        {selected && (
          <motion.div ref={expandedRef} key={selected.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            style={{ border: '1px solid var(--hairline)', background: 'var(--forest2)', overflow: 'hidden', marginBottom: 20, boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>

            <PhotoHero dest={selected} ratio="22 / 10">
              <div style={{ position: 'absolute', top: 12, right: 12, background: 'var(--candle)', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 10, padding: '4px 10px', boxShadow: '0 0 14px rgba(201,151,58,0.4)' }}>Save {selected.saving}</div>
              <div style={{ position: 'absolute', bottom: 12, left: 14 }}>
                {selected.country && (
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.14em', color: 'var(--candle2)', textTransform: 'uppercase', marginBottom: 3 }}>
                    {selected.flag} {selected.country} <span style={{ color: 'rgba(255,255,255,0.5)', margin: '0 3px' }}>›</span> <span style={{ color: 'rgba(255,255,255,0.85)' }}>{selected.name}</span>
                  </div>
                )}
                <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 22, color: '#fff', letterSpacing: '0.05em' }}>{selected.name}</div>
                <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2, fontStyle: 'italic' }}>{selected.faith}</div>
                {selected.stops.length > 0 && (
                  <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 4, fontStyle: 'italic' }}>
                    {selected.stops.slice(0, 4).map((s) => s.name.split(' — ')[0].split(',')[0]).join(' · ')}
                  </div>
                )}
              </div>
            </PhotoHero>

            <div style={{ fontFamily: 'var(--serif-body)', fontSize: 13, color: 'var(--ivory-dim)', lineHeight: 1.75, fontStyle: 'italic', padding: '14px 16px 4px' }}>
              {selected.description}
            </div>

            {/* Gallery + Facts */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '16px 16px 4px' }}>
              <DestinationGallery dest={selected} />

              {selected.facts && selected.facts.length > 0 && (
                <div>
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 8 }}>
                    ◈ Destination Facts
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {selected.facts.map((f, i) => (
                      <div key={i} style={{ border: '1px solid var(--hairline)', background: 'rgba(248,245,239,0.03)', padding: '10px 11px' }}>
                        <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 17, color: 'var(--candle2)', letterSpacing: '0.02em' }}>{f.value}</div>
                        <div style={{ fontFamily: 'var(--serif-body)', fontSize: 10, color: 'var(--ivory-faint)', marginTop: 2, fontStyle: 'italic', lineHeight: 1.3 }}>{f.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Why you'll love it */}
            {selected.loveIt && selected.loveIt.length > 0 && (
              <div style={{ padding: '16px 16px 4px' }}>
                <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 8 }}>
                  ◈ Why You'll Love It
                </div>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {selected.loveIt.map((line, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontFamily: 'var(--serif-body)', fontSize: 13, color: 'var(--ivory)', lineHeight: 1.6, fontStyle: 'italic' }}>
                      <span style={{ color: 'var(--candle)', flexShrink: 0, marginTop: 1 }}>◈</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Stats row (kept from original) */}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Object.keys(selected.stats).length},1fr)`, borderTop: '1px solid var(--hairline)', borderBottom: '1px solid var(--hairline)', marginTop: 14 }}>
              {Object.entries(selected.stats).map(([k, v]) => (
                <div key={k} style={{ padding: '11px 13px', borderRight: '1px solid var(--hairline)' }}>
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                  <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 16, color: 'var(--ivory)' }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Deals — restyled as rows with network badge + save chip. onClick wiring unchanged. */}
            <div style={{ padding: '14px 16px 4px' }}>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 8 }}>
                ◈ Pilgrim Partner Offers — Respectfully Curated
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {selected.deals.map((deal, i) => (
                  <motion.button key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                    onClick={() => openDeal(deal, selected.name)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--hairline)', cursor: 'pointer', textAlign: 'left' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,151,58,0.06)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ width: 34, height: 34, background: 'var(--forest3)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{deal.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 11, color: 'var(--ivory)', letterSpacing: '0.03em' }}>{deal.title}</div>
                      <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory-faint)', marginTop: 1, fontStyle: 'italic' }}>{deal.detail}</div>
                      <div style={{ display: 'inline-block', fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--candle2)', background: 'rgba(201,151,58,0.08)', border: '1px solid var(--hairline2)', padding: '1px 6px', letterSpacing: '0.06em', marginTop: 3, textTransform: 'uppercase' }}>via {NETWORK_LABELS[deal.network] || deal.network}</div>
                    </div>
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid var(--hairline2)', color: 'var(--candle2)', whiteSpace: 'nowrap', flexShrink: 0 }}>{deal.saving}</div>
                  </motion.button>
                ))}
              </div>
            </div>

            <button onClick={goJourney} style={{ display: 'block', width: '100%', padding: 14, marginTop: 12, background: 'var(--candle)', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', boxShadow: '0 0 20px rgba(201,151,58,0.35)' }}>
              See my sacred journey →
            </button>
          </motion.div>
        )}
      </div>
      </div>
    </div>
  )
}
